import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../auth/authStore'
import { getExchangeRates, type ExchangeRates } from '../../shared/api/exchangeRates.api'

const STORAGE_KEY = 'crm:rates-reminder:lastShown'
const FORCE_PARAM = 'show-rates-reminder'

function getMoscowDate(now: Date = new Date()): { dateStr: string; hour: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return {
    dateStr: `${get('year')}-${get('month')}-${get('day')}`,
    hour: parseInt(get('hour'), 10),
  }
}

function ratesUpdatedToday(rates: ExchangeRates, todayStr: string): boolean {
  const dates = [
    rates.updatedAtByCurrency.USD,
    rates.updatedAtByCurrency.EUR,
    rates.updatedAtByCurrency.CNY,
  ]
  return dates.some((d) => {
    if (!d) return false
    const { dateStr } = getMoscowDate(new Date(d))
    return dateStr === todayStr
  })
}

export function useRatesReminder() {
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'ADMIN'

  const { data: rates } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: getExchangeRates,
    enabled: isAdmin,
    staleTime: 60_000,
  })

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!isAdmin || !rates) return

    const { dateStr: todayStr, hour } = getMoscowDate()
    const params = new URLSearchParams(window.location.search)
    const force = params.get(FORCE_PARAM) === '1'

    if (force) {
      setOpen(true)
      return
    }

    if (hour < 10) return
    if (ratesUpdatedToday(rates, todayStr)) return

    let lastShown: string | null = null
    try {
      lastShown = localStorage.getItem(STORAGE_KEY)
    } catch {
      /* localStorage может быть заблокирован */
    }
    if (lastShown === todayStr) return

    setOpen(true)
  }, [isAdmin, rates])

  const close = () => {
    const { dateStr: todayStr } = getMoscowDate()
    try {
      localStorage.setItem(STORAGE_KEY, todayStr)
    } catch {
      /* noop */
    }
    setOpen(false)
  }

  return { open, close }
}
